/**
 * Base API Configuration for RTK Query
 * Provides centralized API setup with authentication and error handling
 */

import type {
	BaseQueryFn,
	FetchArgs,
	FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { API_CONFIG, tokenStorage } from "../../config/api";

/**
 * Retry configuration for failed requests
 */
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

/**
 * Retryable status codes (network errors and server errors that might be transient)
 */
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/**
 * Check if an error is retryable
 */
const isRetryableError = (error: FetchBaseQueryError | undefined): boolean => {
	if (!error) return false;

	// Network / parsing errors are retryable
	if (error.status === "FETCH_ERROR" || error.status === "PARSING_ERROR") {
		return true;
	}

	// Check if status code is retryable
	if (typeof error.status === "number") {
		return RETRYABLE_STATUS_CODES.includes(error.status);
	}

	return false;
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Base query with authentication
 * Automatically adds JWT token to requests if available
 */
const baseQuery = fetchBaseQuery({
	baseUrl: API_CONFIG.baseUrl,
	timeout: API_CONFIG.timeout,
	prepareHeaders: (headers) => {
		const token = tokenStorage.getAccessToken();

		if (token) {
			headers.set("Authorization", `Bearer ${token}`);
		}

		headers.set("Content-Type", "application/json");
		return headers;
	},
});

/**
 * Base query with token refresh logic and retry mechanism
 * - Adds JWT token
 * - Retries transient failures with exponential backoff
 * - Handles 401 by attempting token refresh
 * - Skips redirect for public endpoints
 */
const baseQueryWithReauth: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (args, api, extraOptions) => {
	// Check if this is a public endpoint (should not require auth)
	const url = typeof args === "string" ? args : args.url;
	const isPublicEndpoint =
		url?.includes("/public/") ||
		url?.includes("/property-viewings/public/") ||
		url?.includes("/auth/login") ||
		url?.includes("/auth/register") ||
		url?.includes("/auth/validate-email") ||
		url?.includes("/auth/password/reset/");

	// Check if this is a protected endpoint that might be called from public pages
	const isProtectedEndpoint = url?.includes("/auth/profile");

	// Create a custom baseQuery with timeout override if provided in args
	const queryArgs = typeof args === "string" ? { url: args } : args;
	const customBaseQuery = queryArgs.timeout
		? fetchBaseQuery({
				baseUrl: API_CONFIG.baseUrl,
				timeout: queryArgs.timeout,
				prepareHeaders: (headers) => {
					const token = tokenStorage.getAccessToken();
					if (token) {
						headers.set("Authorization", `Bearer ${token}`);
					}
					headers.set("Content-Type", "application/json");
					return headers;
				},
		  })
		: baseQuery;

	let result = await customBaseQuery(args, api, extraOptions);
	let retryCount = 0;

	// Handle 403 Forbidden errors gracefully (user account inactive or insufficient permissions)
	// Don't retry 403 errors - they're not transient
	// This prevents 403 errors from being retried and cluttering logs
	if (result.error && result.error.status === 403) {
		// For protected endpoints (like profile), clear tokens if we get 403
		// This handles cases where user account is inactive or token is invalid
		if (isProtectedEndpoint) {
			// Clear tokens if we get 403 on profile endpoint (likely inactive account or invalid token)
			tokenStorage.clearTokens();
		}
		// Return the error without retrying - 403 is not a transient error
		return result;
	}

	// Retry logic for transient errors (only for retryable errors, not 403)
	while (isRetryableError(result.error) && retryCount < MAX_RETRIES) {
		retryCount += 1;
		const delay = RETRY_DELAY * Math.pow(2, retryCount - 1); // exponential backoff

		// Only log retries in development mode
		if (process.env.NODE_ENV === "development") {
			// eslint-disable-next-line no-console
			console.warn(
				`API request failed, retrying (${retryCount}/${MAX_RETRIES}) in ${delay}ms`,
				result.error,
			);
		}

		await sleep(delay);
		result = await customBaseQuery(args, api, extraOptions);
	}

	// If we get a 401 Unauthorized, try to refresh the token
	// But skip for public endpoints (they shouldn't return 401 anyway)
	if (result.error && result.error.status === 401 && !isPublicEndpoint) {
		const refreshToken = tokenStorage.getRefreshToken();

		if (refreshToken) {
			// Try to refresh the token
			const refreshResult = await baseQuery(
				{
					url: "/auth/refresh",
					method: "POST",
					body: { refresh: refreshToken },
				},
				api,
				extraOptions,
			);

			if (refreshResult.data) {
				// Store the new token
				const { access } = refreshResult.data as { access: string };
				const currentRefresh = tokenStorage.getRefreshToken();
				if (currentRefresh) {
					tokenStorage.setTokens(access, currentRefresh);
				}

				// Retry the original request
				result = await customBaseQuery(args, api, extraOptions);
			} else {
				// Refresh failed - clear tokens
				tokenStorage.clearTokens();
				// Only redirect if not already on login page and not on a public page
				const isPublicPage =
					window.location.pathname === "/" ||
					window.location.pathname.startsWith("/properties/") ||
					window.location.pathname === "/register";
				if (window.location.pathname !== "/login" && !isPublicPage) {
					window.location.href = "/login";
				}
			}
		} else {
			// No refresh token available - clear tokens
			tokenStorage.clearTokens();
			// Only redirect if not already on login page and not on a public page
			const isPublicPage =
				window.location.pathname === "/" ||
				window.location.pathname.startsWith("/properties/") ||
				window.location.pathname === "/register";
			if (window.location.pathname !== "/login" && !isPublicPage) {
				window.location.href = "/login";
			}
		}
	}

	return result;
};

/**
 * Base API slice for RTK Query
 * All API endpoints will be injected into this base API
 */
export const baseApi = createApi({
	reducerPath: "api",
	baseQuery: baseQueryWithReauth,
	tagTypes: [
		"Properties",
		"Property",
		"User",
		"Auth",
		"Payments",
		"Maintenance",
		"Notifications",
		"Conversations",
		"Organizations",
		"Transactions",
		"Analytics",
		"AIConversations",
		"PropertyViewings",
		"Leases",
		"Announcements",
		"PropertyRequests",
		"PaymentAccounts",
		"PaymentProofs",
		"TenancyAgreements",
	],
	endpoints: () => ({}),
});
